// CB:BEGIN fr-001-privacy-tab
import React from 'react';
import { useCoreBidsSettings } from '../../hooks/useCoreBidsSettings';
import { SettingsSection } from '../settings/SettingsSection';
import { SettingsCheckbox } from '../settings/SettingsCheckbox';
import { SettingsSaveBar } from '../settings/SettingsSaveBar';

export default function CoreBidsPrivacyTab() {
  const s = useCoreBidsSettings();

  // Stop Clerk modal from closing on clicks (bubble phase only, on tab root)
  const stopModalBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (s.loading) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
      </div>
    );
  }

  if (!s.data) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', color: '#dc2626' }}>
          {s.error || 'Failed to load settings'}
        </div>
      </div>
    );
  }

  const settings = s.data.userSettings;

  const handleToggle = (key: keyof typeof settings, checked: boolean) => {
    s.updateLocal({
      userSettings: {
        ...settings,
        [key]: checked,
      },
    });
  };

  const handleSave = async () => {
    try {
      await s.save();
    } catch (err) {
      // Error is already set in hook
    }
  };

  return (
    <div onClick={stopModalBubble} className="corebids-settings" style={{ padding: '24px', maxWidth: '600px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>
        Privacy Settings
      </h2>

      {s.error && !s.error.startsWith('✅') && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {s.error}
        </div>
      )}

      <SettingsSection title="Contact Preferences">
        <div className="mb-5">
          <SettingsCheckbox
            id="direct-contact"
            label="Allow Direct Contact"
            description="Allow contractors to contact you directly via phone or email. When disabled, all communication must go through the platform."
            checked={settings.directContactEnabled}
            onCheckedChange={(checked) => handleToggle('directContactEnabled', checked)}
            testId="privacy-direct-contact"
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="mask-contact"
            label="Mask Contact Information"
            description="Hide your phone number and email address from other users. Only show partial information when necessary."
            checked={settings.maskContactInfo}
            onCheckedChange={(checked) => handleToggle('maskContactInfo', checked)}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Address Privacy">
        <div className="mb-5">
          <SettingsCheckbox
            id="hide-address"
            label="Hide Address Until Bid Accepted"
            description="Keep your full address private from contractors until you accept a bid on your project."
            checked={settings.hideAddressUntilBidAccepted}
            onCheckedChange={(checked) => handleToggle('hideAddressUntilBidAccepted', checked)}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Messaging Preferences">
        <div className="mb-5">
          <SettingsCheckbox
            id="verified-contractors"
            label="Verified Contractors Only"
            description="Only allow verified contractors to send you messages through the platform."
            checked={settings.verifiedContractorsOnlyMessaging}
            onCheckedChange={(checked) => handleToggle('verifiedContractorsOnlyMessaging', checked)}
          />
        </div>
      </SettingsSection>

      <SettingsSection title="Information Sharing">
        <div className="mb-5">
          <SettingsCheckbox
            id="share-insurance"
            label="Share Insurance Claim Information"
            description="Allow contractors to see if you're using insurance to fund your project."
            checked={settings.shareInsuranceClaimInfo}
            onCheckedChange={(checked) => handleToggle('shareInsuranceClaimInfo', checked)}
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="allow-financing"
            label="Allow Financing Offers"
            description="Receive information about financing options for your project."
            checked={settings.allowFinancingOffers}
            onCheckedChange={(checked) => handleToggle('allowFinancingOffers', checked)}
          />
        </div>
      </SettingsSection>

      <SettingsSaveBar
        dirty={s.dirty}
        saving={s.saving}
        error={s.error}
        lastSavedAt={s.lastSavedAt}
        onSave={handleSave}
        onRevert={s.resetLocalToServer}
      />
    </div>
  );
}
// CB:END fr-001-privacy-tab
